import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ValidationError } from 'yup';
import { useNavigate } from 'react-router-dom';
import {
  Controller,
  useForm,
  useWatch,
  type Control,
  type FieldErrors,
  type Resolver,
  type UseFormClearErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import {
  assertionOptions,
  classificationOptions,
  controlNatureOptions,
  controlTypeOptions,
  frequencyOptions,
  significanceOptions,
} from '../../../utils/controlConstants.js';
import { useTheme } from '../../../contexts/ThemeContext.tsx';
import { useAuditYear } from '../../../contexts/AuditYearContext.tsx';
import { useCreateControl, usePatchControlUpdateRiskMappings, useUpdateControl } from '../../../hooks/controls/useControls.ts';
import { TENANT_PATHS } from '../../../routing/paths.ts';
import { InlineEditActions } from '../../../shared/common/InlineEditActions.tsx';
import { Select } from '../../../components/common';
import { FormField } from '../../../components/form/FormField.tsx';
import { SelectInput } from '../../../components/form/inputs/SelectInput.tsx';
import { TextAreaInput } from '../../../components/form/inputs/TextAreaInput.tsx';
import { TextInput } from '../../../components/form/inputs/TextInput.tsx';
import ReportMultiSelect from '../../../components/ReportMultiSelect.js';
import { Sparkles } from 'lucide-react';
import ControlWordingEnrichModal from './ControlWordingEnrichModal.js';
import {
  CONFIGURE_TEST_FIELD_LABELS,
  controlRecordToFormValues,
  formatControlSourceLabel,
  formatControlStatusLabel,
  formatFrequencyLabel,
  formValuesToApiPayload,
  getMissingConfigureTestFieldsFromValues,
  type ConfigureTestRequiredField,
} from '../functional.ts';
import {
  ControlRiskMitigatedSection,
  type ControlRiskMitigatedSectionHandle,
} from './ControlRiskMitigatedSection.tsx';
import {
  controlConfigureTestSchema,
  controlFormSchema,
  defaultControlFormValues,
  type ControlFormValues,
} from '../schema.ts';
import { ControlItgcCreateFields } from './ControlItgcCreateFields.tsx';
import { ControlCreateRiskMitigatedSection } from './ControlCreateRiskMitigatedSection.tsx';

export type PrepareForConfigureTestResult = {
  ready: boolean;
  /** True when PUT /sox-controls ran before opening configure-test. */
  didPersist: boolean;
};

export type ControlOverviewCardHandle = {
  /** Validates required fields; persists only when edit mode or unsaved changes. */
  prepareForConfigureTest: () => Promise<PrepareForConfigureTestResult>;
};

const ReadonlyField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <FormField label={label}>
    <div className="text-sm text-gray-900">{children}</div>
  </FormField>
);

/** Polished multi-select styles aligned with form inputs (SelectInput / TextInput). */
const ASSERTION_SELECT_STYLES = {
  control: (base: Record<string, unknown>, state: { isFocused?: boolean; isDisabled?: boolean }) => ({
    ...base,
    minHeight: 42,
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    backgroundColor: state.isDisabled ? '#f9fafb' : '#ffffff',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none',
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
    },
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 40,
    padding: '2px 12px',
    justifyContent: 'flex-start',
  }),
  placeholder: (base: Record<string, unknown>) => ({
    ...base,
    color: '#9ca3af',
    fontSize: '0.875rem',
  }),
  dropdownIndicator: (base: Record<string, unknown>) => ({
    ...base,
    color: '#6b7280',
    padding: '0 8px',
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    borderRadius: '0.5rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.06)',
    zIndex: 9999,
  }),
  menuList: (base: Record<string, unknown>) => ({
    ...base,
    padding: 6,
    maxHeight: 280,
    overflowY: 'auto',
  }),
  option: (base: Record<string, unknown>, state: { isFocused?: boolean; isSelected?: boolean }) => ({
    ...base,
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    color: '#111827',
    backgroundColor: state.isSelected ? '#eff6ff' : state.isFocused ? '#f9fafb' : '#ffffff',
    border: state.isSelected ? '1px solid #bfdbfe' : '1px solid transparent',
    padding: '8px 10px',
    cursor: 'pointer',
    ':active': {
      backgroundColor: '#dbeafe',
    },
  }),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 9999,
  }),
};

const ASSERTION_MULTI_CHECKBOX_CLASS_NAMES = {
  optionContent: 'flex min-w-0 items-center gap-2.5 leading-5',
  checkbox:
    'h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0',
  optionLabel: 'min-w-0 flex-1 text-sm text-gray-800',
  summary: 'inline-flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden text-sm font-medium text-gray-900',
  summaryName: 'min-w-0 truncate',
  summaryCount: 'shrink-0 whitespace-nowrap text-sm font-semibold text-blue-600',
};

const bindField = (
  name: keyof ControlFormValues,
  register: UseFormRegister<ControlFormValues>,
  clearErrors?: UseFormClearErrors<ControlFormValues>
) => {
  const { onChange, ...rest } = register(name);
  return {
    ...rest,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      clearErrors?.(name);
      void onChange(event);
    },
  };
};

const OptionalSelectField: React.FC<{
  name: 'controlType' | 'frequency' | 'significance' | 'classification' | 'controlNature';
  label: string;
  placeholder?: string;
  options: { value: string; label: string }[];
  error?: string;
  formControl: Control<ControlFormValues>;
  clearErrors?: UseFormClearErrors<ControlFormValues>;
  hideEmptySelectOption?: boolean;
}> = ({
  name,
  label,
  placeholder,
  options,
  error,
  formControl,
  clearErrors,
  hideEmptySelectOption = false,
}) => (
  <Controller
    name={name}
    control={formControl}
    render={({ field }) => (
      <SelectInput
        id={name}
        label={label}
        placeholder={hideEmptySelectOption ? undefined : placeholder}
        options={options}
        error={error}
        value={field.value ?? ''}
        onChange={(e) => {
          clearErrors?.(name);
          field.onChange(e.target.value);
        }}
        onBlur={field.onBlur}
      />
    )}
  />
);

interface ControlFormFieldsProps {
  register: UseFormRegister<ControlFormValues>;
  control: Control<ControlFormValues>;
  errors: FieldErrors<ControlFormValues>;
  clearErrors?: UseFormClearErrors<ControlFormValues>;
  readOnly?: boolean;
  currentValues?: Partial<ControlFormValues>;
  detailsHeaderSlot?: React.ReactNode;
  /** Rendered at the bottom of the overview card (same white container as form fields). */
  inCardFooterSlot?: React.ReactNode;
  headerActions?: React.ReactNode;
  /** System control id (read-only in view/edit). */
  systemControlId?: string;
  sourceLabel?: string;
  statusLabel?: string;
  updatedLabel?: string;
  /** Edit mode: omit blank "Select …" options from dropdowns. */
  hideEmptySelectOption?: boolean;
  /** Raw control record — used by the AI wording enrichment modal. */
  controlData?: Record<string, unknown>;
  /** Keep classification visible but non-editable in form mode. */
  classificationReadOnly?: boolean;
  /** Current wording value (for the enrichment modal's "before"). */
  currentWording?: string;
  /** Called with the AI-improved wording; parent sets the form field. */
  onEnrichAccept?: (text: string) => void;
  setValue?: UseFormSetValue<ControlFormValues>;
}

export const ControlFormFields: React.FC<ControlFormFieldsProps> = ({
  register,
  control: formControl,
  errors,
  clearErrors,
  readOnly = false,
  currentValues,
  detailsHeaderSlot,
  inCardFooterSlot,
  headerActions,
  systemControlId,
  sourceLabel,
  statusLabel,
  updatedLabel,
  hideEmptySelectOption = false,
  controlData,
  classificationReadOnly = false,
  currentWording,
  onEnrichAccept,
  setValue,
}) => {
  const hasMetaRow = Boolean(systemControlId || sourceLabel || statusLabel || updatedLabel);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const classification = useWatch({ control: formControl, name: 'classification' });
  const showItgcCreateFields = !readOnly && classification === 'itgc' && typeof setValue === 'function';
  const classificationLabel =
    classificationOptions.find((o) => o.value === classification)?.label ??
    String(classification ?? '').trim();
  const controlClassification = String(controlData?.classification ?? currentValues?.classification ?? '').trim().toLowerCase();
  const isITGCControlDetails = controlClassification === 'itgc';
  const itgcDomain = (controlData?.itgc_domain as { code?: string; name?: string } | undefined) ?? undefined;
  const itgcDomainLabel =
    itgcDomain && (itgcDomain.code || itgcDomain.name)
      ? [String(itgcDomain.code ?? '').trim(), String(itgcDomain.name ?? '').trim()].filter(Boolean).join(' - ')
      : '';
  const itgcSystems = Array.isArray(controlData?.itgc_systems)
    ? (controlData?.itgc_systems as Array<{ name?: string; system_id?: string; systemId?: string }>)
    : [];
  const itgcSystemLabel = itgcSystems
    .map((system) => {
      const name = String(system?.name ?? '').trim();
      const code = String(system?.system_id ?? system?.systemId ?? '').trim();
      if (name && code) return `${name} (${code})`;
      return name || code;
    })
    .filter(Boolean)
    .join(', ');

  const frequencySelectOptions = frequencyOptions.map((f) => ({
    value: f,
    label: formatFrequencyLabel(f),
  }));

  const detailsFields = readOnly ? (
    <>
      {hasMetaRow && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
          {systemControlId ? (
            <ReadonlyField label="System Control ID">
              {systemControlId}
            </ReadonlyField>
          ) : null}
          {updatedLabel ? (
            <ReadonlyField label="Last Updated">{updatedLabel}</ReadonlyField>
          ) : null}
          {sourceLabel ? (
            <ReadonlyField label="Source">
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-700 bg-blue-50 border border-blue-100 rounded">
                {sourceLabel}
              </span>
            </ReadonlyField>
          ) : null}
          {statusLabel ? (
            <ReadonlyField label="Status">
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold tracking-wide text-gray-700 bg-gray-100 border border-gray-200 rounded capitalize">
                {statusLabel}
              </span>
            </ReadonlyField>
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
        <ReadonlyField label="Control Id">
          {currentValues?.controlId || <span className="text-gray-400">-</span>}
        </ReadonlyField>
        <ReadonlyField label="Title">
          {currentValues?.title || <span className="text-gray-400">-</span>}
        </ReadonlyField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 pt-3 border-t border-gray-200">
        <ReadonlyField label="Type">
          {currentValues?.controlType ? (
            controlTypeOptions.find((o) => o.value === currentValues.controlType)?.label ?? currentValues.controlType
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </ReadonlyField>
        <ReadonlyField label="Frequency">
          {currentValues?.frequency ? formatFrequencyLabel(currentValues.frequency) : <span className="text-gray-400">-</span>}
        </ReadonlyField>
        <ReadonlyField label="Significance">
          {currentValues?.significance ? (
            significanceOptions.find((o) => o.value === currentValues.significance)?.label ?? currentValues.significance
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </ReadonlyField>
        <ReadonlyField label="Classification">
          {currentValues?.classification ? (
            classificationOptions.find((o) => o.value === currentValues.classification)?.label ?? currentValues.classification
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </ReadonlyField>
        <ReadonlyField label="Control Nature">
          {currentValues?.controlNature ? (
            controlNatureOptions.find((o) => o.value === currentValues.controlNature)?.label ?? currentValues.controlNature
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </ReadonlyField>
        <ReadonlyField label="MRC (Management review control)">
          {currentValues?.mrcControl ? 'Yes' : 'No'}
        </ReadonlyField>
        <ReadonlyField label="Department">
          {currentValues?.department || <span className="text-gray-400">-</span>}
        </ReadonlyField>
        <ReadonlyField label="Control Owner">
          {currentValues?.controlOwner || <span className="text-gray-400">-</span>}
        </ReadonlyField>
        <ReadonlyField label="Key Reports">
          {(() => {
            const reports = Array.isArray(controlData?.key_reports)
              ? (controlData?.key_reports as Array<{ id?: string; name?: string }>)
              : Array.isArray(controlData?.keyReports)
                ? (controlData?.keyReports as Array<{ id?: string; name?: string }>)
                : [];
            const labeled = reports
              .map((r) => ({
                id: String(r?.id ?? '').trim(),
                name: String(r?.name ?? '').trim(),
              }))
              .filter((r) => r.name);
            if (labeled.length > 0) {
              return (
                <div className="flex flex-wrap gap-1">
                  {labeled.map((r) => (
                    <span
                      key={r.id || r.name}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                    >
                      {r.name}
                    </span>
                  ))}
                </div>
              );
            }
            return <span className="text-gray-400">-</span>;
          })()}
        </ReadonlyField>
        <ReadonlyField label="External auditor reliance">
          {currentValues?.externalAuditorReliance === null || currentValues?.externalAuditorReliance === undefined ? (
            <span className="text-gray-400">-</span>
          ) : currentValues.externalAuditorReliance ? (
            'Yes'
          ) : (
            'No'
          )}
        </ReadonlyField>
      </div>

      <ReadonlyField label="Assertions">
        {currentValues?.assertions && currentValues.assertions.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {currentValues.assertions.map((a) => (
              <span
                key={a}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 capitalize"
              >
                {assertionOptions.find((o) => o.value === a)?.label ?? a}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </ReadonlyField>
      {isITGCControlDetails ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          <ReadonlyField label="Domain">
            {itgcDomainLabel || <span className="text-gray-400">-</span>}
          </ReadonlyField>
          <ReadonlyField label="Systems">
            {itgcSystemLabel || <span className="text-gray-400">-</span>}
          </ReadonlyField>
        </div>
      ) : null}

      <div className="pt-3 border-t border-gray-200 space-y-3">
        <ReadonlyField label="Control Wording">
          <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap break-words">
            {currentValues?.controlWordingText || <span className="text-gray-400">-</span>}
          </p>
        </ReadonlyField>

        <ReadonlyField label="Objective">
          <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap break-words">
            {currentValues?.objective || <span className="text-gray-400">-</span>}
          </p>
        </ReadonlyField>
      </div>
    </>
  ) : (
    <>
      {hasMetaRow && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {systemControlId ? (
            <ReadonlyField label="System Control ID">{systemControlId}</ReadonlyField>
          ) : null}
          {updatedLabel ? (
            <ReadonlyField label="Last Updated">{updatedLabel}</ReadonlyField>
          ) : null}
          {sourceLabel ? (
            <ReadonlyField label="Source">{sourceLabel}</ReadonlyField>
          ) : null}
          {statusLabel ? (
            <ReadonlyField label="Status">{statusLabel}</ReadonlyField>
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <TextInput
          id="controlId"
          label="Control Id"
          required
          placeholder="e.g. OTC-01"
          error={errors.controlId?.message}
          {...bindField('controlId', register, clearErrors)}
        />
        <TextInput
          id="title"
          label="Title"
          required
          placeholder="e.g. Three-Way Match Control"
          error={errors.title?.message}
          {...bindField('title', register, clearErrors)}
        />
      </div>

      <div className="relative">
        {!readOnly && onEnrichAccept && (
          <button
            type="button"
            onClick={() => setEnrichOpen(true)}
            className="absolute right-0 top-0 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            <Sparkles className="h-3.5 w-3.5" /> Enrich with AI
          </button>
        )}
        <TextAreaInput
          id="controlWordingText"
          label="Control Wording"
          required
          rows={5}
          placeholder="Full control description / wording..."
          error={errors.controlWordingText?.message}
          {...bindField('controlWordingText', register, clearErrors)}
        />
      </div>

      {enrichOpen && (
        <ControlWordingEnrichModal
          control={controlData}
          currentWording={currentWording}
          onAccept={(text) => onEnrichAccept?.(text)}
          onClose={() => setEnrichOpen(false)}
        />
      )}

      <TextAreaInput
        id="objective"
        label="Objective"
        rows={3}
        placeholder="Describe the objective of this control..."
        error={errors.objective?.message}
        {...bindField('objective', register, clearErrors)}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 pt-4 border-t border-gray-200">
        {classificationReadOnly ? (
          <ReadonlyField label="Classification">
            {classificationLabel ? (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-700 bg-blue-50 border border-blue-100 rounded">
                {classificationLabel}
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </ReadonlyField>
        ) : (
          <OptionalSelectField
            name="classification"
            label="Classification"
            options={classificationOptions}
            error={errors.classification?.message}
            formControl={formControl}
            clearErrors={clearErrors}
            hideEmptySelectOption
          />
        )}
        <OptionalSelectField
          name="significance"
          label="Significance"
          options={significanceOptions}
          error={errors.significance?.message}
          formControl={formControl}
          clearErrors={clearErrors}
          hideEmptySelectOption
        />
        <OptionalSelectField
          name="controlType"
          label="Type"
          options={controlTypeOptions}
          error={errors.controlType?.message}
          formControl={formControl}
          clearErrors={clearErrors}
          hideEmptySelectOption
        />
        <OptionalSelectField
          name="frequency"
          label="Frequency"
          placeholder="Select frequency"
          options={frequencySelectOptions}
          error={errors.frequency?.message}
          formControl={formControl}
          clearErrors={clearErrors}
        />
        <OptionalSelectField
          name="controlNature"
          label="Control Nature"
          placeholder="Select control nature"
          options={controlNatureOptions}
          error={errors.controlNature?.message}
          formControl={formControl}
          clearErrors={clearErrors}
        />
        <TextInput
          id="department"
          label="Department"
          placeholder="e.g. Finance"
          error={errors.department?.message}
          {...bindField('department', register, clearErrors)}
        />
        <TextInput
          id="controlOwner"
          label="Control Owner"
          placeholder="e.g. Finance Manager"
          error={errors.controlOwner?.message}
          {...bindField('controlOwner', register, clearErrors)}
        />
        <Controller
          name="keyReportIds"
          control={formControl}
          render={({ field }) => {
            const knownKeyReports = Array.isArray(controlData?.key_reports)
              ? (controlData?.key_reports as Array<{ id?: string; name?: string }>)
              : Array.isArray(controlData?.keyReports)
                ? (controlData?.keyReports as Array<{ id?: string; name?: string }>)
                : [];
            return (
              <FormField label="Key Reports" error={errors.keyReportIds?.message as string | undefined} htmlFor="keyReportIds">
                <ReportMultiSelect
                  value={Array.isArray(field.value) ? field.value : []}
                  onChange={(next) => {
                    clearErrors?.('keyReportIds');
                    field.onChange(next);
                  }}
                  listParams={{ key: 'true', is_report: 'true' }}
                  knownReports={knownKeyReports}
                  showAddNew={false}
                  placeholder="Select key reports..."
                />
              </FormField>
            );
          }}
        />
      </div>

      {showItgcCreateFields ? (
        <ControlItgcCreateFields
          enabled
          formControl={formControl}
          errors={errors}
          clearErrors={clearErrors}
          setValue={setValue}
        />
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
        <Controller
          name="mrcControl"
          control={formControl}
          render={({ field }) => (
            <SelectInput
              id="mrcControl"
              label="MRC (Management review control)"
              options={[
                { value: 'no', label: 'No' },
                { value: 'yes', label: 'Yes' },
              ]}
              value={field.value ? 'yes' : 'no'}
              onChange={(e) => {
                clearErrors?.('mrcControl');
                field.onChange(e.target.value === 'yes');
              }}
              onBlur={field.onBlur}
            />
          )}
        />
        <Controller
          name="externalAuditorReliance"
          control={formControl}
          render={({ field }) => (
            <SelectInput
              id="externalAuditorReliance"
              label="External auditor reliance"
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
              value={field.value === true ? 'yes' : 'no'}
              onChange={(e) => {
                clearErrors?.('externalAuditorReliance');
                field.onChange(e.target.value === 'yes');
              }}
              onBlur={field.onBlur}
            />
          )}
        />
        <Controller
          name="assertions"
          control={formControl}
          render={({ field }) => {
            const assertionError = errors.assertions?.message as string | undefined;
            return (
              <FormField label="Assertions" error={assertionError} htmlFor="assertions">
                <Select
                  inputId="assertions"
                  aria-label="Assertions"
                  className={assertionError ? 'ring-1 ring-red-500 rounded-lg' : 'w-full'}
                  options={assertionOptions}
                  value={field.value ?? []}
                  onChange={(value) => {
                    clearErrors?.('assertions');
                    field.onChange(Array.isArray(value) ? value : []);
                  }}
                  onBlur={field.onBlur}
                  isMulti
                  isMultiCheckbox
                  isSearchable
                  multiCheckboxSummaryTooltip
                  multiCheckboxClassNames={ASSERTION_MULTI_CHECKBOX_CLASS_NAMES}
                  styles={{
                    ...ASSERTION_SELECT_STYLES,
                    control: (base, state) => {
                      const styled = ASSERTION_SELECT_STYLES.control(base, state);
                      return {
                        ...styled,
                        borderColor: assertionError
                          ? '#ef4444'
                          : state.isFocused
                            ? '#3b82f6'
                            : '#d1d5db',
                        boxShadow: assertionError
                          ? '0 0 0 2px rgba(239, 68, 68, 0.15)'
                          : state.isFocused
                            ? '0 0 0 2px rgba(59, 130, 246, 0.2)'
                            : 'none',
                      };
                    },
                  }}
                  placeholder={hideEmptySelectOption ? 'Assertions' : 'Select assertions'}
                  placeholderOnFocus={hideEmptySelectOption ? 'Search' : 'Search assertions'}
                  placeholderOnBlur={hideEmptySelectOption ? 'Assertions' : 'Select assertions'}
                  noOptionsMessage={() => 'No assertions match your search'}
                  menuPlacement="top"
                />
              </FormField>
            );
          }}
        />
      </div>
    </>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900">Control Overview</h3>
        </div>
        {headerActions ? <div className="flex-shrink-0">{headerActions}</div> : null}
      </div>
      <div className="p-6 space-y-4">
        {detailsHeaderSlot}
        {detailsFields}
        {inCardFooterSlot ? (
          <div className="pt-4 border-t border-gray-200">{inCardFooterSlot}</div>
        ) : null}
      </div>
    </div>
  );
};

export { formatControlSourceLabel, formatControlStatusLabel };

export default ControlFormFields;

/** Manual create form — same pattern as {@link RiskCreatePanel}. */
export const ControlCreatePanel: React.FC<{
  detailsHeaderSlot?: React.ReactNode;
  returnTo?: string;
}> = ({ detailsHeaderSlot, returnTo = TENANT_PATHS.SOX_CONTROLS }) => {
  const navigate = useNavigate();
  const { primaryColor } = useTheme();
  const { selectedAuditYearId: createAuditYearId } = useAuditYear();
  const createControlMutation = useCreateControl();

  const {
    register,
    control,
    handleSubmit,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<ControlFormValues>({
    resolver: yupResolver(controlFormSchema),
    defaultValues: defaultControlFormValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const primaryButtonStyle = primaryColor
    ? { backgroundColor: primaryColor, borderColor: primaryColor }
    : undefined;
  const createClassification = useWatch({ control, name: 'classification' });
  const createITGCDomainID = useWatch({ control, name: 'itgcDomainId' });
  const [createRiskMappings, setCreateRiskMappings] = useState<
    { riskId: string; processIds: string[] }[]
  >([]);
  const [createItgcRiskIds, setCreateItgcRiskIds] = useState<string[]>([]);

  return (
    <form onSubmit={handleSubmit(async (values) => {
      try {
        const isBusinessClassification =
          String(values.classification ?? '').trim().toLowerCase() === 'business';
        if (isBusinessClassification) {
          const hasInvalidRiskProcessMapping = createRiskMappings.some(
            (mapping) =>
              String(mapping.riskId ?? '').trim() !== '' &&
              (!Array.isArray(mapping.processIds) || mapping.processIds.length === 0)
          );
          if (hasInvalidRiskProcessMapping) {
            toast.error('Each risk must have at least one mapped process.');
            return;
          }
        }
        await createControlMutation.mutateAsync({
          formValues: values,
          riskMappings: createRiskMappings,
          itgcRiskIds: createItgcRiskIds,
          auditYearId: createAuditYearId ?? undefined,
        });
        navigate(returnTo);
      } catch {
        /* toast in hook */
      }
    })} className="space-y-5">
      <ControlFormFields
        register={register}
        control={control}
        errors={errors}
        clearErrors={clearErrors}
        setValue={setValue}
        detailsHeaderSlot={
          detailsHeaderSlot ?? (
            <p className="text-sm text-gray-500">
              Title and control wording are required. The control is saved as a draft until required fields are set.
            </p>
          )
        }
        inCardFooterSlot={(
          <ControlCreateRiskMitigatedSection
            classification={createClassification}
            itgcDomainId={createITGCDomainID}
            onBusinessRiskMappingsChange={setCreateRiskMappings}
            onITGCRiskIdsChange={setCreateItgcRiskIds}
          />
        )}
      />
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={() => navigate(returnTo)}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={createControlMutation.isPending}
          className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          style={primaryButtonStyle}
        >
          {createControlMutation.isPending ? 'Saving...' : 'Create'}
        </button>
      </div>
    </form>
  );
};

/** View / edit overview on control detail — same pattern as risk inline edit. */
export const ControlOverviewCard = forwardRef<
  ControlOverviewCardHandle,
  {
    control: Record<string, unknown>;
    controlUuid: string;
    onSaved: () => void;
  }
>(({ control, controlUuid, onSaved }, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<ControlFormValues>(defaultControlFormValues);
  const { selectedAuditYearId } = useAuditYear();
  const updateControlMutation = useUpdateControl();
  const patchControlUpdateRiskMappingsMutation = usePatchControlUpdateRiskMappings();
  const riskSectionRef = useRef<ControlRiskMitigatedSectionHandle>(null);

  const {
    register,
    control: formControl,
    handleSubmit,
    reset,
    getValues,
    setValue,
    trigger,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<ControlFormValues>({
    resolver: yupResolver(controlConfigureTestSchema) as Resolver<ControlFormValues>,
    defaultValues: defaultControlFormValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const editITGCDomainID = useWatch({ control: formControl, name: 'itgcDomainId' });

  useEffect(() => {
    const next = controlRecordToFormValues(control);
    setInitialFormValues(next);
    reset(next);
  }, [control, reset]);

  const persistControlOnly = useCallback(
    async (values: ControlFormValues) => {
      await updateControlMutation.mutateAsync({
        controlUuid,
        payload: formValuesToApiPayload(values, { controlUuid }),
      });
      setIsEditing(false);
      setInitialFormValues(values);
      onSaved();
    },
    [controlUuid, onSaved, updateControlMutation]
  );

  const persistControlUpdateRiskMappings = useCallback(
    async (values: ControlFormValues) => {
      let riskMappings;
      try {
        riskMappings = await riskSectionRef.current?.buildWirePayload();
      } catch (error) {
        if (error instanceof Error && error.message) {
          toast.error(error.message);
        }
        throw error;
      }
      if (!riskMappings) {
        const message = 'Failed to prepare risk mappings';
        toast.error(message);
        throw new Error(message);
      }
      await patchControlUpdateRiskMappingsMutation.mutateAsync({
        controlUuid,
        payload: {
          ...formValuesToApiPayload(values, {
            controlUuid,
            itgcRiskIds: riskMappings
              .map((row) => String(row.risk_id ?? '').trim())
              .filter(Boolean),
          }),
          risk_mappings: riskMappings,
          ...(selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {}),
        },
      });
      setIsEditing(false);
      setInitialFormValues(values);
      onSaved();
    },
    [controlUuid, onSaved, patchControlUpdateRiskMappingsMutation, selectedAuditYearId]
  );

  const handleCancelEdit = useCallback(() => {
    reset(initialFormValues);
    riskSectionRef.current?.resetForm();
    setIsEditing(false);
  }, [initialFormValues, reset]);

  const applyConfigureValidationErrors = useCallback(
    async (err: unknown) => {
      setIsEditing(true);
      await Promise.resolve();
      if (err instanceof ValidationError) {
        err.inner.forEach((issue) => {
          if (issue.path) {
            setError(issue.path as keyof ControlFormValues, {
              type: 'manual',
              message: issue.message,
            });
          }
        });
        return;
      }
      await trigger(undefined, { shouldFocus: true });
    },
    [setError, trigger]
  );

  useImperativeHandle(
    ref,
    () => ({
      prepareForConfigureTest: async () => {
        clearErrors();
        const values = getValues();

        const configureFieldMap: Record<ConfigureTestRequiredField, keyof ControlFormValues> = {
          title: 'title',
          controlWording: 'controlWordingText',
          objective: 'objective',
        };
        const missingConfigure = getMissingConfigureTestFieldsFromValues(values);
        if (missingConfigure.length > 0) {
          setIsEditing(true);
          missingConfigure.forEach((key) => {
            setError(configureFieldMap[key], {
              type: 'manual',
              message: `${CONFIGURE_TEST_FIELD_LABELS[key]} is required to configure test`,
            });
          });
          return { ready: false, didPersist: false };
        }

        try {
          await controlConfigureTestSchema.validate(values, { abortEarly: false });
        } catch (err) {
          await applyConfigureValidationErrors(err);
          return { ready: false, didPersist: false };
        }

        if (!isEditing && !isDirty) {
          return { ready: true, didPersist: false };
        }

        try {
          await persistControlOnly(values);
          return { ready: true, didPersist: true };
        } catch {
          return { ready: false, didPersist: false };
        }
      },
    }),
    [
      applyConfigureValidationErrors,
      clearErrors,
      getValues,
      isDirty,
      isEditing,
      persistControlOnly,
      setError,
    ]
  );

  const riskMitigatedSection = (
    <ControlRiskMitigatedSection
      ref={riskSectionRef}
      control={control}
      controlUuid={controlUuid}
      itgcDomainId={String(editITGCDomainID ?? '')}
      isEditing={isEditing}
      hideInlineActions
    />
  );

  const isSaving = updateControlMutation.isPending || patchControlUpdateRiskMappingsMutation.isPending;

  const systemControlId = String(control.control_id ?? '').trim();
  const sourceLabel = formatControlSourceLabel(String(control.source ?? control.import_source ?? ''));
  const statusLabel = formatControlStatusLabel(String(control.status ?? 'draft'));
  const updatedLabel = control.last_edited
    ? new Date(String(control.last_edited)).toLocaleDateString()
    : '';

  const meta = {
    systemControlId,
    sourceLabel,
    statusLabel,
    updatedLabel,
  };

  if (!isEditing) {
    const isSeededControl = Boolean(control?.is_seeded ?? control?.isSeeded);
    return (
      <div className="space-y-6">
        <ControlFormFields
          readOnly
          currentValues={initialFormValues}
          controlData={control}
          classificationReadOnly
          register={register}
          control={formControl}
          errors={errors}
          clearErrors={clearErrors}
          {...meta}
          headerActions={
            <InlineEditActions
              isEditing={false}
              onEdit={() => setIsEditing(true)}
              editDisabled={isSeededControl}
              editDisabledReason={
                isSeededControl ? 'General ITGC control cannot be updated.' : undefined
              }
            />
          }
          inCardFooterSlot={riskMitigatedSection}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSubmit(async (values) => {
          try {
            await persistControlUpdateRiskMappings(values);
          } catch {
            /* validation toast in persistControlUpdateRiskMappings; API toast in hook */
          }
        })}
        className="space-y-6"
      >
        <ControlFormFields
          register={register}
          control={formControl}
          errors={errors}
          clearErrors={clearErrors}
          setValue={setValue}
          classificationReadOnly
          hideEmptySelectOption
          controlData={control}
          currentWording={getValues('controlWordingText')}
          onEnrichAccept={(text: string) =>
            setValue('controlWordingText', text, { shouldDirty: true, shouldValidate: true })
          }
          {...meta}
          headerActions={
            <InlineEditActions
              isEditing
              isSaving={isSaving}
              onCancel={handleCancelEdit}
              saveLabel="Save"
            />
          }
          inCardFooterSlot={riskMitigatedSection}
        />
      </form>
    </div>
  );
});

ControlOverviewCard.displayName = 'ControlOverviewCard';
